"use client";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";

interface OtpPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onVerify: (otp: string) => void;
  onResend: () => void;
  email?: string;
  isLoading?: boolean;
  error?: string | null;
}

export function OtpPopup({
  isOpen,
  onClose,
  onVerify,
  onResend,
  email,
  isLoading = false,
  error,
}: OtpPopupProps) {
  const t = useTranslations("auth");
  const [otp, setOtp] = useState("");
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes in seconds

  useEffect(() => {
    if (!isOpen) {
      setOtp("");
      setTimeLeft(600);
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length === 6) {
      onVerify(otp);
    }
  };

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setOtp(value);
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="relative">
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
            disabled={isLoading}
          >
            <X size={20} />
          </button>
          <CardTitle className="text-center">{t("otpCode")}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="text-center text-sm text-muted-foreground">
              <p>{t("otpInstructions")}</p>
              {email && (
                <p className="mt-1 font-medium">
                  {email.replace(/(.{2})(.*)(@.*)/, "$1***$3")}
                </p>
              )}
            </div>

            {error && (
              <div className="bg-destructive/15 text-destructive px-3 py-2 rounded-md text-sm text-center">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="otp">{t("otpCode")}</Label>
              <Input
                id="otp"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder={t("otpPlaceholder")}
                value={otp}
                onChange={handleOtpChange}
                className="text-center text-lg tracking-widest"
                disabled={isLoading}
                autoFocus
              />
            </div>

            <div className="text-center text-sm text-muted-foreground">
              {timeLeft > 0 ? (
                <p>{t("codeExpiresIn", { time: formatTime(timeLeft) })}</p>
              ) : (
                <p className="text-destructive">{t("codeExpired")}</p>
              )}
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onResend}
                disabled={isLoading || timeLeft > 540} // Allow resend after 1 minute
                className="flex-1"
              >
                {t("otpResend")}
              </Button>
              <Button
                type="submit"
                disabled={otp.length !== 6 || isLoading || timeLeft === 0}
                className="flex-1"
              >
                {isLoading ? t("verifying") : t("otpVerify")}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
